/**
 * ToggleSwitch — THE on/off slider. One implementation, both surfaces.
 *
 * WHY THIS EXISTS
 * ---------------
 * The product had three different answers to "show me an on/off control":
 *
 *   1. `WatchMailboxSwitch` — a hand-rolled Tailwind slider, `bg-blossom` when
 *      on. The one an AM actually meets, on the monitored-mailboxes page.
 *   2. `Buttons/IOSSwitch` — a styled MUI Switch, `#FFC600` when on. Used in
 *      exactly one place (ProspectDrawer), and a different yellow.
 *   3. A bare `<input type="checkbox">` — no styling at all, so the browser
 *      paints its own blue. This is what "Process mail for real" and "Draft the
 *      reply email" were, and it is why two controls that do the same kind of
 *      thing looked like they came from different products.
 *
 * A raw checkbox is not a neutral choice. It renders in the OS accent colour,
 * which is outside the design system, differs per user, and on Windows is the
 * blue that reads as "this is a form, not our product".
 *
 * WHICH YELLOW
 * ------------
 * `blossom` (#FFA400) when on, matching `WatchMailboxSwitch` — the slider
 * already in front of AMs, and the colour the shared OptionRow fills a selected
 * radio/checkbox with in BOTH ora and the add-in. `IOSSwitch` uses `sunrise`
 * (#FFC600) instead; that is a real inconsistency, left alone here because
 * changing a component this one does not replace is not this change's business.
 *
 * WHY A BUTTON AND NOT A STYLED CHECKBOX
 * --------------------------------------
 * `role="switch"` on a button is the accessible pattern for a control whose two
 * states are on/off rather than checked-in-a-set, and it lets the thumb animate
 * without fighting a native control's appearance. The cost is that it submits
 * NO form value — every caller here is a controlled React component driving a
 * callback, which is why that is affordable. A future caller inside a plain
 * `<form>` needs a hidden input beside it.
 *
 * ONE SIZE (38x22 track, 18px thumb), chosen to sit comfortably in the Outlook
 * task pane — a ~320px column where a larger control crowds its row — and used
 * unchanged in ora so the two surfaces match. There is deliberately no `size`
 * prop and no `className` passthrough: the five hand-rolled sliders this
 * replaced came in three sizes and two yellows precisely because each one could
 * be tweaked where it was used. If a surface genuinely needs another size, add
 * a NAMED variant here rather than an escape hatch.
 *
 * BOTH APPS COMPILE THIS. The add-in's tailwind config globs
 * `../../app/common/**`, so the classes below are generated in the add-in
 * bundle too. Anything added here must use tokens that exist in BOTH configs
 * (`sunrise` / `blossom` / `primary-gray-light-*` do).
 */

export interface ToggleSwitchProps {
  /** Current state. Controlled — this component holds none of its own. */
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible name. Required: the visual carries no text of its own. */
  label: string;
  disabled?: boolean;
  /** Id of prose describing the control's state or a refusal. */
  describedBy?: string;
  /**
   * `progress` shows the wait cursor instead of not-allowed — for a control
   * disabled because a change is IN FLIGHT rather than because it is
   * unavailable. The two mean different things to someone who just clicked.
   */
  disabledReason?: "unavailable" | "progress";
  id?: string;
  "data-testid"?: string;
  /**
   * Native tooltip. Optional and rarely needed — added for the intake-mode
   * switch's "Auto is managed by an administrator" case (story 26725), where
   * the reason a disabled control is disabled is not obvious from its label
   * alone.
   */
  title?: string;
}

export default function ToggleSwitch({
  checked,
  onChange,
  label,
  disabled = false,
  describedBy,
  disabledReason = "unavailable",
  id,
  "data-testid": testId,
  title,
}: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      title={title}
      className={`relative inline-flex h-[22px] w-[38px] flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-sunrise focus:ring-offset-2 disabled:opacity-60 ${
        disabledReason === "progress"
          ? "disabled:cursor-progress"
          : "disabled:cursor-not-allowed"
      } ${checked ? "bg-blossom" : "bg-primary-gray-light-300"}`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow ring-0 transition ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}
